// ─── Accès aux calendriers (section de Profil) ────────────────────
// Injectée dans EcranProfil (prop `sections`, `acces:true` → rendue dans
// « Qui voit quoi »). Chaque calendrier de l'espace actif reçoit la carte
// unifiée `SectionAcces` (3 niveaux). Remplace la matrice voir/écrire/config
// qui vivait dans la Config du calendrier (appui long) — l'accès se gère
// désormais au MÊME endroit que les inventaires et les plans.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useUI, useT, useEspace, membresDe, SectionAcces } from '@mop/core';
import {
  ecouterCalendriersSuite, accesCalendrierN, definirAccesCalendrierN, assurerAccesCalendrier,
} from './services/calendriersEspace';

export default function SectionAccesCalendriers() {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { espaceActif } = useEspace();

  const [calendriers, setCals] = useState([]);
  // Une écoute refusée renvoyait une liste vide EN SILENCE : la section
  // affichait « aucun calendrier » alors qu'elle n'était pas lisible. On
  // remonte la cause, comme sur le Journal, la messagerie et les contacts.
  const [erreurLecture, setErreurLecture] = useState('');
  const membres = useMemo(() => membresDe(espaceActif), [espaceActif]);
  const membresUids = useMemo(() => membres.map(m => m.uid), [membres]);
  const ownerUid = espaceActif?.creePar;

  useEffect(() => {
    if (!espaceActif?.id) { setCals([]); return undefined; }
    setErreurLecture('');
    return ecouterCalendriersSuite(espaceActif.id, (l) => {
      setCals(l);
      // Migration douce : matérialise accesN (comportement historique préservé
      // via { '*': 'contributeur' } pour les calendriers sans accesN).
      l.forEach(c => assurerAccesCalendrier(c, membresUids));
    }, setErreurLecture);
  }, [espaceActif?.id, membresUids.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  if (erreurLecture) {
    return (
      <View style={s.bandeau}>
        <Text style={s.bandeauTxt}>⚠️ {t('calendrier.erreurLectureListe')}</Text>
        <Text style={s.bandeauDet}>{erreurLecture}</Text>
      </View>
    );
  }
  if (!calendriers.length) {
    return <Text style={s.vide}>{t('calendrier.aucunCal')}</Text>;
  }

  return (
    <View>
      {calendriers.map(c => (
        <SectionAcces key={c.id} titre={c.nom || t('calendrier.titre')} emoji={c.emoji || '📅'}
          acces={accesCalendrierN(c)} membres={membres} ownerUid={ownerUid}
          onChange={(acces) => definirAccesCalendrierN(c.id, acces, membresUids).catch(() => {})} />
      ))}
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  vide: { fontSize: 12.5, color: UI.muted, paddingVertical: 8 },
  bandeau:    { backgroundColor: UI.dangerTint || '#fdecec', borderRadius: 12, borderWidth: 0.5, borderColor: UI.danger || '#e5484d', padding: 11 },
  bandeauTxt: { fontSize: 13, fontWeight: '800', color: UI.danger || '#e5484d' },
  bandeauDet: { fontSize: 11.5, color: UI.muted, marginTop: 3 },
});
